import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useDeleteLocale } from "./use-locales";
import type { SupportedLocale } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly target: SupportedLocale | null;
  readonly onOpenChange: (open: boolean) => void;
}

export function DeleteLocaleDialog({ target, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const remove = useDeleteLocale();

  useEffect(() => {
    if (target) setError(null);
  }, [target]);

  const handleSubmit = async () => {
    if (!target || remove.isPending) return;
    setError(null);
    try {
      await remove.mutateAsync(target.code);
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={target != null} onOpenChange={(next) => !remove.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-delete-locale-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.locales.delete.title")}</DialogTitle>
          <DialogDescription>
            {t("compliance.locales.delete.description", {
              code: target?.code ?? "",
            })}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p data-testid="compliance-delete-locale-error" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={remove.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            data-testid="compliance-delete-locale-submit"
            onClick={handleSubmit}
            disabled={remove.isPending}
          >
            {remove.isPending ? t("compliance.actions.deleting") : t("compliance.actions.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
